// frontend/src/pages/SignupPage.js
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../api'
import { setAuth } from '../auth'
import { useCurrentBasho } from '../hooks/useCurrentBasho'

export default function SignupPage() {
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [bashoLocked, setBashoLocked] = useState(false)
  const [loading, setLoading] = useState(false)
  const { basho, day } = useCurrentBasho()

  const handleSubmit = async e => {
    e.preventDefault()
    setError('')
    setBashoLocked(false)
    if (password !== confirm) { setError('Passwords do not match.'); return }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return }
    setLoading(true)
    try {
      const res = await api.post('/auth/signup', { username, password, email })
      setAuth(res.data, true)
      window.location.href = `/picks/${basho}/${day}`
    } catch (err) {
      if (err.response?.status === 423) {
        setBashoLocked(true)
      } else {
        setError(err.response?.data?.error || 'Signup failed')
      }
    } finally {
      setLoading(false)
    }
  }

  if (bashoLocked) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <Link to="/" style={s.backHome}>← Home</Link>
          <div style={s.sealWrap}>
            <div style={s.seal}><span style={s.sealText}>相撲{'\n'}番付</span></div>
          </div>
          <div style={s.logoArea}>
            <h1 style={s.logoText}>Basho in Progress</h1>
            <p style={s.tagline}>Sumo Pick'em</p>
          </div>
          <div style={s.lockedNotice}>
            <div style={s.lockedIcon}>🏟️</div>
            <p style={s.lockedText}>
              Sign-ups are paused while the basho is in progress. Check back once the tournament ends!
            </p>
          </div>
          <p style={s.footer}>
            Already have an account?{' '}
            <Link to="/login" style={s.link}>Sign in</Link>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <Link to="/" style={s.backHome}>← Home</Link>

        <div style={s.sealWrap}>
          <div style={s.seal}><span style={s.sealText}>相撲{'\n'}番付</span></div>
        </div>

        <div style={s.logoArea}>
          <h1 style={s.logoText}>Create Account</h1>
          <p style={s.tagline}>Sumo Pick'em</p>
        </div>

        <form onSubmit={handleSubmit} style={s.form}>
          <div style={s.field}>
            <label style={s.label}>Username</label>
            <input
              value={username}
              onChange={e => setUsername(e.target.value)}
              required autoFocus style={s.input}
              placeholder="choose a username"
            />
          </div>
          <div style={s.field}>
            <label style={s.label}>Email</label>
            <input
              type="email" value={email}
              onChange={e => setEmail(e.target.value)}
              required style={s.input} placeholder="you@example.com"
            />
          </div>
          <div style={s.field}>
            <label style={s.label}>Password</label>
            <input
              type="password" value={password}
              onChange={e => setPassword(e.target.value)}
              required style={s.input} placeholder="min. 6 characters"
            />
          </div>
          <div style={s.field}>
            <label style={s.label}>Confirm Password</label>
            <input
              type="password" value={confirm}
              onChange={e => setConfirm(e.target.value)}
              required style={s.input} placeholder="••••••••"
            />
          </div>
          {error && <p style={s.error}>{error}</p>}
          <button type="submit" disabled={loading} style={s.btn}>
            {loading ? 'Creating account…' : 'Sign Up'}
          </button>
        </form>

        <p style={s.footer}>
          Already have an account?{' '}
          <Link to="/login" style={s.link}>Sign in</Link>
        </p>
      </div>
    </div>
  )
}

const font = "'Noto Serif JP', Georgia, serif"
const ink = '#1A0A00'
const muted = '#8A7A5A'
const accent = '#C41E3A'

const s = {
  page: {
    minHeight: '100vh',
    background: '#F5F0E8',
    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(180,160,120,0.06) 3px, rgba(180,160,120,0.06) 4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  card: {
    background: '#FFFFFF', border: '1px solid #D4C8A8', borderRadius: 4,
    padding: '40px 36px', width: '100%', maxWidth: 380,
    boxShadow: '0 4px 24px rgba(42,26,14,0.08)', position: 'relative',
  },
  backHome: {
    position: 'absolute', top: 18, left: 22,
    color: muted, textDecoration: 'none', fontFamily: font, fontSize: 12, letterSpacing: 1,
  },
  sealWrap: { textAlign: 'center', marginBottom: 12 },
  seal: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 48, height: 48, background: accent, borderRadius: 4,
  },
  sealText: {
    color: '#F5F0E8', fontSize: 9, fontWeight: 700, textAlign: 'center',
    lineHeight: 1.3, whiteSpace: 'pre', fontFamily: font,
  },
  logoArea: { textAlign: 'center', marginBottom: 24 },
  logoText: { fontFamily: font, fontSize: 22, fontWeight: 900, color: ink, margin: '0 0 4px' },
  tagline: { color: muted, fontFamily: font, fontSize: 12, margin: 0, letterSpacing: 1 },
  form: { display: 'flex', flexDirection: 'column', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 10, color: muted, fontFamily: font, letterSpacing: 1, textTransform: 'uppercase' },
  input: {
    padding: '10px 12px', background: '#FDFBF7',
    border: '1px solid #D4C8A8', borderRadius: 2,
    color: ink, fontSize: 14, fontFamily: font, outline: 'none',
    width: '100%', boxSizing: 'border-box',
  },
  error: { color: accent, fontFamily: font, fontSize: 12, margin: 0 },
  lockedNotice: {
    background: '#F5F0E8', border: '1px solid #D4C8A8', borderRadius: 4,
    padding: '24px 20px', textAlign: 'center', margin: '8px 0 20px',
  },
  lockedIcon: { fontSize: 32, marginBottom: 12 },
  lockedText: {
    color: ink, fontFamily: font, fontSize: 13, margin: 0, lineHeight: 1.6,
  },
  btn: {
    padding: '12px', background: accent, color: '#F5F0E8',
    border: 'none', borderRadius: 2, fontSize: 11, fontWeight: 700,
    fontFamily: font, cursor: 'pointer', letterSpacing: 3, textTransform: 'uppercase',
    marginTop: 4,
  },
  footer: { textAlign: 'center', marginTop: 18, color: muted, fontFamily: font, fontSize: 12 },
  link: { color: accent, textDecoration: 'none' },
}
